import { useCallback, useRef, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import Icon from '../../components/Icon';
import WarningGlyph from '../../components/WarningGlyph';
import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { formatTime } from '../../shared/taxonomy';
import { trackDrag } from './dragEdge';
import { formatDuration, MODE_NAME } from './travel';
import { CONFLICT, TRAVEL_BORDER_WIDTH, TRAVEL_FILL, TRAVEL_STROKE } from './travelStyle';
import { DRAG_STEP_MINUTES, legEnd, travelBounds, type TravelLeg } from './travelLegs';

/**
 * The two halves of a journey, drawn separately.
 *
 * `Window` is the time available: a dashed outline with no fill, from the end
 * of the last thing to the start of the next. `Required` is the journey itself,
 * always at its true length — if it does not fit the window it hangs out of the
 * bottom and over the next event, which is precisely the thing worth seeing.
 *
 * Required is draggable within its window in quarter-hours, so the schedule can
 * say "I'll leave at half past" rather than only ever showing the latest
 * possible departure.
 */

const Window = styled('div')({
  position: 'absolute',
  borderRadius: 14,
  border: `${TRAVEL_BORDER_WIDTH}px dashed ${TRAVEL_STROKE}`,
  background: 'none',
  pointerEvents: 'none',
});

const Required = styled('div', { shouldForwardProp: (p) => p !== '$dragging' })<{
  $dragging: boolean;
}>(({ $dragging }) => ({
  position: 'absolute',
  // The stage above is transparent to the pointer so clicks on empty sky reach
  // the slot catcher; anything clickable inside it opts back in.
  pointerEvents: 'auto',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '8px 12px',
  borderRadius: 14,
  background: TRAVEL_FILL,
  border: `${TRAVEL_BORDER_WIDTH}px solid ${TRAVEL_STROKE}`,
  color: BRAND.parchment,
  fontFamily: FONTS.body,
  // The whole block moves, so the whole block wears the grab cursor. What it
  // cannot say on its own is that a press without a drag opens the mode
  // dialog; the tooltip says that.
  cursor: $dragging ? 'grabbing' : 'grab',
  overflow: 'hidden',
  userSelect: 'none',
  touchAction: 'none',
  // Lifted while dragging so it slides over its neighbours rather than under.
  zIndex: $dragging ? 6 : 3,
  boxShadow: $dragging ? '0 6px 18px rgba(0,0,0,0.35)' : 'none',
  '&:hover': { borderColor: '#8d2b40' },
}));

const Lines = styled('div')({
  minWidth: 0,
  flex: 1,
  lineHeight: 1.25,
});

/**
 * The phone's block, which is tapped rather than dragged, so it has no grip.
 *
 * Its label wraps instead of running off the edge: a block beside another
 * event is half the column wide, and "Travel Time: 1 hr 56 min" on one line
 * showed as "Travel Time: 1 h" however tall the block was. Once it is too
 * narrow for the icons beside the words, they move above them and the words
 * get the whole width.
 */
const PhoneBody = styled('div')({
  flex: 1,
  minWidth: 0,
  display: 'grid',
  gridTemplateColumns: 'auto minmax(0, 1fr) auto',
  gridTemplateAreas: '"warn lines mode"',
  alignItems: 'center',
  columnGap: 10,
  rowGap: 4,
  '& p': { whiteSpace: 'normal', overflowWrap: 'anywhere' },
  '@container (max-width: 210px)': {
    gridTemplateColumns: 'auto minmax(0, 1fr)',
    gridTemplateAreas: '"warn mode" "lines lines"',
    alignContent: 'start',
    alignSelf: 'stretch',
    justifyItems: 'start',
  },
});

/**
 * The grip: the advertisement for the drag, no longer the only way in.
 *
 * The block is two controls sharing one box — it can be moved, and it can be
 * clicked to change how you are travelling — and for a while the drag was
 * restricted to this grip so the two could not be confused. Aiming at a 20px
 * target to move a block is a worse deal than the ambiguity was, so the whole
 * block drags now and a press that never moved is read as a click. The grip
 * stays because it is what makes the block *look* draggable: a column of grip
 * lines is the one convention every calendar and board app shares, and it
 * reads before anyone has hovered anything.
 */
const Grip = styled('div', { shouldForwardProp: (p) => p !== '$dragging' })<{
  $dragging: boolean;
}>(({ $dragging }) => ({
  flexShrink: 0,
  alignSelf: 'center',
  display: 'flex',
  alignItems: 'center',
  padding: '5px 1px',
  // Negative margins rather than a smaller block padding: the grip sits nearer
  // the edge and pulls the mode icon out with it, without moving the text.
  marginLeft: -4,
  marginRight: -7,
  borderRadius: 5,
  cursor: $dragging ? 'grabbing' : 'grab',
  touchAction: 'none',
  color: 'rgba(248,238,234,0.9)',
  background: $dragging ? 'rgba(255,255,255,0.16)' : 'transparent',
  '&:hover': { background: 'rgba(255,255,255,0.16)' },
}));

/**
 * Everywhere this block could be put, shown only while it is being moved.
 *
 * The reachable range is often just fifteen or twenty minutes, and nothing on
 * screen said so — a user could drag until it stopped and not know whether it
 * had hit a limit or a bug. This shades the whole sweep and labels both ends,
 * so the answer to "how far does this go" is visible for exactly as long as
 * the question is being asked.
 */
const Slack = styled('div')({
  position: 'absolute',
  left: 0,
  right: 0,
  borderRadius: 14,
  background: 'rgba(173,111,124,0.22)',
  border: '1px dashed rgba(248,238,234,0.5)',
  pointerEvents: 'none',
  zIndex: 2,
});

const SlackEdge = styled('span')({
  position: 'absolute',
  right: 8,
  fontFamily: FONTS.body,
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: '0.02em',
  color: BRAND.parchment,
  textShadow: '0 1px 3px rgba(0,0,0,0.5)',
  whiteSpace: 'nowrap',
});

export interface Geometry {
  /** Pixels from the top of the column for a given minute of the day. */
  offset: (minutes: number) => number;
  /** Pixels per minute, for turning a drag distance into time. */
  scale: number;
}

/**
 * A mini day's journey: the same two boxes, read-only, labelled with the time
 * and the mode at the size the rest of a mini day is written in.
 */
const MiniRequired = styled('div')({
  position: 'absolute',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  padding: '1px 6px',
  borderRadius: 7,
  background: TRAVEL_FILL,
  border: `1.5px solid ${TRAVEL_STROKE}`,
  color: BRAND.parchment,
  fontFamily: FONTS.body,
  fontSize: 9,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  pointerEvents: 'none',
  zIndex: 3,
});

interface Props {
  leg: TravelLeg;
  geometry: Geometry;
  /** Percentage left/width for the required block, from the shared lane pass. */
  lane: { left: string; width: string };
  /** Absent on a phone, where the block is tapped rather than dragged. */
  onDrag?: (id: string, startMinutes: number) => void;
  /** Absent in a mini day, where the journey is shown and not changed. */
  onOpen?: (leg: TravelLeg) => void;
  mini?: boolean;
}

export default function TravelBlock({ leg, geometry, lane, onDrag, onOpen, mini = false }: Props) {
  const [dragging, setDragging] = useState(false);

  const windowTop = geometry.offset(leg.windowStart);
  const windowHeight = geometry.offset(leg.windowEnd) - windowTop;
  const requiredTop = geometry.offset(leg.startMinutes);
  const requiredHeight = geometry.offset(legEnd(leg)) - requiredTop;

  /**
   * The whole sweep the block can cover: from its earliest start to its latest
   * end. Wider than the window whenever the journey overhangs, which is the
   * case where knowing the limits matters most.
   */
  const bounds = travelBounds(leg.windowStart, leg.windowEnd, leg.estimate.minutes);
  /** Nowhere to move it (a journey exactly as long as its gap, or to or from the hotel): no drag, no grip. */
  const fixed = bounds.earliest === bounds.latest;
  const slackTop = geometry.offset(bounds.earliest);
  const slackHeight = geometry.offset(bounds.latest + leg.estimate.minutes) - slackTop;

  /**
   * Whether the press that is ending was a drag.
   *
   * Every press on the block is a potential drag, so the click that follows
   * one has to be told apart from a click that stood still — otherwise moving
   * a block always ends by opening the mode dialog. Three pixels of travel is
   * the line: below it, a hand that meant to press.
   */
  const moved = useRef(false);
  const DRAG_SLOP_PX = 3;

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!onDrag || fixed) return;
      e.stopPropagation();
      moved.current = false;
      setDragging(true);

      const originY = e.clientY;
      const originStart = leg.startMinutes;

      trackDrag(
        e,
        (ev) => {
          if (Math.abs(ev.clientY - originY) > DRAG_SLOP_PX) moved.current = true;
          const deltaMinutes = (ev.clientY - originY) / geometry.scale;
          const stepped =
            Math.round((originStart + deltaMinutes) / DRAG_STEP_MINUTES) * DRAG_STEP_MINUTES;
          if (stepped !== leg.startMinutes) onDrag(leg.id, stepped);
        },
        () => setDragging(false),
      );
    },
    [leg.id, leg.startMinutes, geometry.scale, onDrag, fixed],
  );

  /** Keyboard nudging, so the block is not pointer-only. */
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onOpen?.(leg);
        return;
      }
      if (!onDrag || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
      e.preventDefault();
      const step = e.key === 'ArrowUp' ? -DRAG_STEP_MINUTES : DRAG_STEP_MINUTES;
      onDrag(leg.id, leg.startMinutes + step);
    },
    [leg, onDrag, onOpen],
  );

  const { estimate } = leg;
  // Below this the label is a lie rather than a summary: keep the icon only.
  const roomy = requiredHeight >= 46;

  if (mini) {
    return (
      <>
        <Window
          aria-hidden="true"
          style={{
            top: windowTop,
            height: Math.max(windowHeight, 4),
            left: 0,
            right: 0,
            borderRadius: 7,
            borderWidth: 1.5,
          }}
        />
        <MiniRequired
          role="img"
          aria-label={`${formatDuration(estimate.minutes)} journey, ${MODE_NAME[estimate.mode]}`}
          style={{
            top: requiredTop,
            height: Math.max(requiredHeight, 14),
            left: lane.left,
            width: lane.width,
          }}
        >
          {leg.tight && (
            <Box component="span" sx={{ display: 'flex', color: CONFLICT }}>
              <WarningGlyph size={9} />
            </Box>
          )}
          <Icon name={`transport:${estimate.mode}`} size={11} />
          <span>
            {estimate.approximate && '~'}
            {formatDuration(estimate.minutes)}
          </span>
        </MiniRequired>
      </>
    );
  }

  return (
    <>
      <Window
        aria-hidden="true"
        style={{ top: windowTop, height: Math.max(windowHeight, 6), left: 0, right: 0 }}
      />

      {/* Only while moving. At rest it would be one more box in a column that
          already has plenty. */}
      {dragging && (
        <Slack style={{ top: slackTop, height: Math.max(slackHeight, 8) }}>
          <SlackEdge style={{ top: 3 }}>leave {formatTime(bounds.earliest)}</SlackEdge>
          <SlackEdge style={{ bottom: 3 }}>leave {formatTime(bounds.latest)}</SlackEdge>
        </Slack>
      )}

      <Tooltip
        // Silent mid-drag: the block is already saying what it does.
        title={dragging ? '' : fixed ? 'Click to change mode of transport' : 'Drag to adjust - Click to change mode of transport'}
        placement="top"
        arrow
      >
      <Required
        $dragging={dragging}
        role="button"
        tabIndex={0}
        aria-label={
          `${estimate.approximate ? 'About ' : ''}${formatDuration(estimate.minutes)} journey, ` +
          `${MODE_NAME[estimate.mode]}, ${estimate.from} to ${estimate.to}. ` +
          'Change how you travel, or use the arrow keys to move it.'
        }
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        onClick={(e) => {
          e.stopPropagation();
          // The tail of a drag, not a press: leave the dialog closed.
          if (moved.current) {
            moved.current = false;
            return;
          }
          onOpen?.(leg);
        }}
        style={{
          top: requiredTop,
          height: Math.max(requiredHeight, 26),
          left: lane.left,
          width: lane.width,
          // The phone's label lays itself out by the block's own width.
          ...(!onDrag && { containerType: 'inline-size' as const, alignItems: 'stretch' }),
        }}
      >
        {!onDrag ? (
          <PhoneBody>
            {leg.tight && (
              <Box sx={{ gridArea: 'warn', display: 'flex', color: CONFLICT }}>
                <WarningGlyph size={15} />
              </Box>
            )}
            <Lines sx={{ gridArea: 'lines' }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                Travel Time: {estimate.approximate && '~'}
                {formatDuration(estimate.minutes)}
              </Typography>
              {roomy && (
                <Typography sx={{ fontSize: 11.5, opacity: 0.92 }}>
                  {estimate.from} → {estimate.to} · {MODE_NAME[estimate.mode]}
                  {leg.viaOnline && ' · from your last venue'}
                </Typography>
              )}
            </Lines>
            <Box sx={{ gridArea: 'mode', display: 'flex', opacity: 0.95 }}>
              <Icon name={`transport:${estimate.mode}`} size={roomy ? 26 : 20} />
            </Box>
          </PhoneBody>
        ) : (
        <>
        {leg.tight && (
          <Box sx={{ display: 'flex', color: CONFLICT, flexShrink: 0 }}>
            <WarningGlyph size={15} />
          </Box>
        )}

        <Lines>
          <Typography sx={{ fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap' }}>
            {/* The tilde is the whole warning at this size: one character that
                says "about", with the reason a click away in the dialog. */}
            Travel Time: {estimate.approximate && '~'}
            {formatDuration(estimate.minutes)}
          </Typography>
          {roomy && (
            <Typography
              sx={{
                fontSize: 11.5,
                opacity: 0.92,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {estimate.from} → {estimate.to} · {MODE_NAME[estimate.mode]}
              {leg.viaOnline && ' · from your last venue'}
            </Typography>
          )}
        </Lines>

        {/* Centre-right, per the reference, shifted in to leave the edge to
            the grip. */}
        <Box sx={{ display: 'flex', flexShrink: 0, opacity: 0.95 }}>
          <Icon name={`transport:${estimate.mode}`} size={roomy ? 30 : 20} />
        </Box>

        {!fixed && (
          <Grip $dragging={dragging} aria-hidden="true">
            <Icon name="ui:dragHandle" size={roomy ? 26 : 20} />
          </Grip>
        )}
        </>
        )}
      </Required>
      </Tooltip>
    </>
  );
}
